import {inject, Injectable} from "@angular/core";

import { AppPrefabResourceManagerService, WidgetPropertiesProvider} from "@wms/design-container";
import {BaseComponent} from "@wms/components";
import {DialogService, StudioService, UserService, WmHttpService} from "@wms/services";
import { find, forEach, get, isEmpty, isString, keys, set, union } from "lodash-es";
import {
  getClonedObject,
  preventCachingOf,
  loadScripts as _loadScripts,
  getProjectResourcePath,
  loadStyleSheets,
  isNewVerGreaterThanCurrentVer
} from '@wms/core';
import {environment} from "@wms/app";
import { PrefabService, PrefabCacheService, PageService } from "@wms/services";
import { runtimeEventManager, stringStartsWith, stringEndsWith, getDecodedData, getValidJSON, replace } from "@wm/core";
import { Subscription } from "rxjs";
import { BaseVariableService } from "@wms/variables";
import { ArtifactsService } from '@wms/settings/artifacts';
import { WmsWorkspaceManagerService } from "../../services/wms-workspace-manager.service";

/* callback function when prefab resource is loaded */
class OnPrefabResourceLoad {
  private count: number;
  constructor(count: number) {
    this.count = count;
  }

  updateCount () {
    this.count -= 1;
  };

  getCount () {
    return this.count;
  };
}
let showPrefabs = true;
let destroyListener: any;
@Injectable({
  providedIn: 'root'
})
export class PrefabManager extends BaseComponent {
  private wp: WidgetPropertiesProvider;
  public prefabSkipEventSubGroups = ['prefablifecycleevents'];
  public loadingComponents:boolean | string = '';
  public prefabsLoaded:boolean = false;
  private wmHttpService: WmHttpService;
  public studioPrefabNamePropertiesMap: {[key: string]: any} = {};
  private studioPrefabNameConfigMap: {[key: string]: any} = {};
  public appPrefabNamePropertiesMap: {[key: string]: any} = {};
  private appPrefabNameConfigMap: {[key: string]: any} = {};
  private activePrefabConfig: any;
  private pendingTasks: {[key: string]: any} = {
    resources: {}
  };
  private versionMismatchMessages: {[key: string]: any} = {};
  private prefabScriptCache: {[key: string]: any} = {};
  private appPrefabs: any;
  private workspacePrefabs: any;
  private bundledPrefabs = [];
  private prefabService: PrefabService;
  private prefabCacheService: PrefabCacheService;
  private variableService: BaseVariableService
  private pageService: PageService;
  reloadPrefabs: boolean;
  public userService:UserService
  public studioService: StudioService;
  public user:any;
  public selectedOrganization: any;
  public artifactsService:ArtifactsService;
  public organizationsList: any;
  public upgradePrefabDetails:any;
  private resourceMngr: AppPrefabResourceManagerService;

  constructor(private dialogService: DialogService,private wsManager:WmsWorkspaceManagerService) {
    super();
    this.wp = inject(WidgetPropertiesProvider);
    this.wmHttpService = inject(WmHttpService);
    this.prefabService = inject(PrefabService);
    this.prefabCacheService = inject(PrefabCacheService);
    this.variableService = inject(BaseVariableService);
    this.pageService = inject(PageService);
    this.studioService = inject(StudioService);
    this.artifactsService = inject(ArtifactsService);
    this.userService = inject(UserService);
    this.resourceMngr = inject(AppPrefabResourceManagerService);

    runtimeEventManager.setService('PrefabManager', this);
    runtimeEventManager.setService('BaseVariableService', this.variableService);
    this.registerDestroyListener.add(runtimeEventManager.on('load-prefabs').subscribe(() => {
      this.loadPrefabList();
    }));
  }

  // TODO: implement WMSPreferences, /editor/scripts/modules/preferences/prefences.js
  // let showPrefabs = $P.hasAccess('components.prefabs');
  loadPrefabs(prefabGroup?: any) {
    if (!showPrefabs) {
      return;
    }

    this.loadingComponents = 'prefabs';

    this.processPrefabsList()
      .then(this.processPrefabsForDisplay.bind(this, prefabGroup));
  }

  loadPrefabList(event?:any, group?:any) {
    this.loadingComponents = 'prefabs';

    event?.stopPropagation();
    event?.preventDefault();
    if (!this.prefabsLoaded) {
      return;
    }
    this.prefabsLoaded = false;
    this.fetchProjectPrefabs()
      .then(this.fetchWorkspacePrefabs.bind(this, false))
      .then(this.loadPrefabs.bind(this, group));
  }

  processPrefabsList() {
    let projectPrefabs = Object.keys(this.appPrefabNamePropertiesMap),
      studioPrefabs = Object.keys(this.studioPrefabNamePropertiesMap),
      prefabList = union(projectPrefabs, studioPrefabs),
      mergedPrefabs: {[key: string]: any} = {},
      prefabObj;
    //this will merge the prefabs, first priority is given for the prefabs in project
    forEach(prefabList, (prefabName) => {
      if (this.appPrefabNamePropertiesMap[prefabName]) {
        prefabObj = this.appPrefabNamePropertiesMap[prefabName];
        prefabObj.loadPrefabResource = true;
      } else {
        prefabObj = this.studioPrefabNamePropertiesMap[prefabName];
      }
      if (this.appPrefabNamePropertiesMap[prefabName] && !this.studioPrefabNamePropertiesMap[prefabName]) { //if the prefab is not in studio and exists in the project then categorize it as project prefab
        prefabObj.isProjectPrefab = true;
      }
      if (this.appPrefabNamePropertiesMap[prefabName]) {
        prefabObj.isPrefabInUse = true;
      }
      prefabObj.iconUrl = prefabObj.icon = prefabObj.system || prefabObj.icon.startsWith(environment.SERVICES_BASE_PATH) ? prefabObj.icon : environment.SERVICES_BASE_PATH + prefabObj.icon;
      mergedPrefabs[prefabName] = prefabObj;
    });
    return Promise.resolve(mergedPrefabs);
  }

  processPrefabsForDisplay(prefabGroup?: any, prefabs?: any) {
    let prefabNames = Object.keys(prefabs),
      map: {[key: string]: any} = {},
      prefabSubGroups: any = [],
      prefabInUseSubGroup = this.wp.addSubGroup(
        {
          "name": "in-use",
          "parent": "prefabs",
          "widgets": [],
          "filteredWidgets": []
        }
      );
    if (prefabInUseSubGroup) {
      prefabInUseSubGroup.widgets = [];
      prefabInUseSubGroup.filteredWidgets = [];
    }
    prefabSubGroups.push(prefabInUseSubGroup);

    prefabNames.forEach((prefabName: string) => {
      let prefabDetails = prefabs[prefabName],
        prefabSubGroup,
        prefabObj;
      let project = this.projectService.getDetails();

      //if the prefab project is opened then hide it from the list of prefabs in workspace
      if (prefabDetails.name === project.name && project.type === 'PREFAB') {
        return;
      }

      prefabDetails.category = (prefabDetails.category || 'basic').toLowerCase();

      this.prefabSkipEventSubGroups.push(prefabName + '_' + 'events');
      prefabSubGroup = map[prefabDetails.category];

      if (!prefabSubGroup) {
        prefabSubGroup = this.wp.addSubGroup(
          {
            "name": prefabDetails.category,
            "parent": "prefabs",
            "widgets": [],
            "filteredWidgets": []
          }
        );
        if (prefabSubGroup) {
          prefabSubGroup.widgets = [];
          prefabSubGroup.filteredWidgets = [];
        }

        prefabSubGroups.push(prefabSubGroup);
        map[prefabDetails.category] = prefabSubGroup;
      }

      prefabObj = {
        'id': prefabDetails.id,
        'name': prefabDetails.name,
        'displayName': prefabDetails.displayName || prefabDetails.name,
        'desc': prefabDetails.description,
        'iconUrl': prefabDetails.icon.replace(/:projectId/ig, this.projectService.getProjectId()),
        'category': prefabDetails.category || 'prefabs',
        'isDraggable': true,
        'isProjectPrefab': prefabDetails.isProjectPrefab || false,
        'isDevPrefab': prefabDetails.artifactStatus === 'DEV',
        'isPrefabInUse': prefabDetails.isPrefabInUse || false,
        'widgetType': 'wm-prefab',
        'dropEffectCls': 'block wm-prefab',
        'defaults': {
          'prefabName': prefabName,
          'registration-required': ''
        },
        'version': prefabDetails.version,
        'readableVersion':'',
        'versionId': prefabDetails.versionId || "1.0"
      };

      if(prefabDetails && this.workspacePrefabs){
        const readablePrefab = this.workspacePrefabs.find((pre:any) => pre.name === prefabDetails.name );
        if(readablePrefab && readablePrefab.readableVersion) {
          prefabObj.readableVersion = readablePrefab.readableVersion;
        }
      }


      // @ts-ignore
      prefabObj['_defaults'] = JSON.stringify(prefabObj.defaults);
      prefabSubGroup.widgets.push(prefabObj);
      prefabSubGroup.filteredWidgets.push(prefabObj);

      if (prefabDetails.isPrefabInUse) {
        prefabObj.category = 'in-use';
        prefabSubGroups[0].widgets.push(prefabObj);
        prefabSubGroups[0].filteredWidgets.push(prefabObj);
      }
    });

    this.prefabsLoaded = true;
    this.loadingComponents = false;
    this.reloadPrefabs = true;
    prefabGroup = prefabGroup || this.wp.getGroup('prefabs');
    prefabGroup.subGroups = prefabSubGroups;
  }


  setWorkspacePrefabs(prefabs: any) {
    if (!this.projectService.getDetails()) {
      // TODO: Remove this later when projectdetails are available
      console.log('project details are not yet available to filter prefabs.');
    }
    if (this.projectService.getDetails()?.platformType === 'NATIVE_MOBILE') {
      prefabs = prefabs.filter((p: any) => { return !p.system});
    }
    this.workspacePrefabs = prefabs;
    this.setVersionMismatchMessages();
    this.prepareWorkspacePrefabsNamePropertiesMap();
  }

  prepareWorkspacePrefabsNamePropertiesMap() {
    this.studioPrefabNamePropertiesMap = {};
    forEach(this.workspacePrefabs, (prefabObj: any) => {
      this.studioPrefabNamePropertiesMap[prefabObj.name] = prefabObj;
    });
  }


  prepareAppPrefabsNamePropertiesMap() {
    this.appPrefabNamePropertiesMap = {};
      forEach(this.appPrefabs, (prefabObj) => {
        this.appPrefabNamePropertiesMap[prefabObj.name] = prefabObj
      })
  }

  listStudioPrefabs() {
    this.studioPrefabNamePropertiesMap = {};
    return (this.prefabService.list() as Promise<any>).then(this.onStudioPrefabsLoad.bind(this));
  }

  /*@TODO: Deprecated. to be removed
  * update the studio-prefab properties in studioPrefabNamePropertiesMap
  */
  onStudioPrefabsLoad(prefabs: any) {
    return new Promise((res, rej) => {
      prefabs.forEach((prefab: any) => {
        this.studioPrefabNamePropertiesMap[prefab.name] = prefab;
      });
      res(this.studioPrefabNamePropertiesMap);
    })
  }

  fetchProjectPrefabs(showSpinner: boolean = false) {
    return this.projectService.getProjectPrefabs(showSpinner).then(this.setProjectPrefabs.bind(this));
  }

  setProjectPrefabs(prefabs: any) {
    this.appPrefabs = prefabs;
    this.prepareAppPrefabsNamePropertiesMap();
  }

  getProjectPrefabs() {
    return getClonedObject(this.appPrefabs) || [];
  }

  getWorkspacePrefabs() {
    return getClonedObject(this.workspacePrefabs) || [];
  }

  fetchWorkspacePrefabs(showSpinner: boolean = false) {
    // Native mobile: not fetching the default prefabs like youtube, google maps etc
    return this.projectService.getWorkspacePrefabs(showSpinner).then(this.setWorkspacePrefabs.bind(this));
  }

  getVersionMismatchMessage(prefabName: string) {
    return this.versionMismatchMessages[prefabName];
  }

  setVersionMismatchMessages() {
    forEach(this.getProjectPrefabs(), (prefab: any) => {
      let messageMap = this.versionMismatchMessages,
        prefabName = prefab.name,
        prefabProjectVersion = parseFloat(prefab.version),
        prefabWorkspaceVersion  = parseFloat(prefab.workspaceVersion);
      //if the prefab version is in draft condition show the message
      if (prefab.artifactStatus === 'DEV') {
        messageMap[prefabName] = this.locale.MESSAGE_ARTIFACT_NOT_PUBLISHED;
      } //@TODO:Implement these versions older, future, conflict
      /*else if (_.isNumber(prefabProjectVersion) && _.isNumber(prefabWorkspaceVersion)) { //compare the version if they can be parsed
          if (prefabProjectVersion > prefabWorkspaceVersion) {
              messageMap[prefabName] = $rs.locale.MESSAGE_FUTURE_VERSION_PREFAB;
          } else if (prefabProjectVersion < prefabWorkspaceVersion) {
              messageMap[prefabName] = $rs.locale.MESSAGE_OLDER_VERSION_PREFAB;
          }
      } else if (prefab.version !== prefab.workspaceVersion) { //show the conflict message if they're not equal
          messageMap[prefabName] = $rs.locale.MESSAGE_CONFLICT_VERSION_PREFAB;
      }*/
    });
  }

   addBundledPrefabsToWorkspace() {
     let wkspPrefabs = keys(this.studioPrefabNamePropertiesMap), index, obj, bundledPrefabs: any[] = [];
     forEach(this.appPrefabs, (prefabObj) => {
       // get the list of studio prefabs (artifactstatus=PUBLISHED and GUID is not null)
       // which are not in the workspace prefabs list
       index = wkspPrefabs.indexOf(prefabObj.name);
       if (index === -1 && prefabObj.artifactStatus === "PUBLISHED" && prefabObj.guid != null) {
         obj = {
           "name": prefabObj.name,
           "guid": prefabObj.guid,
           "version": prefabObj.version
         };
         bundledPrefabs.push(obj);
       }
     });
     if (bundledPrefabs.length ) {
       return this.projectService.loadEdnDetails().then((res: any) => {
         let teamProjectId = res.id;
         return this.projectService.addBundledPrefabsToWksp(teamProjectId, bundledPrefabs).then(() => {
         });
       });
     } else {
       return Promise.resolve();
     }
   }



  /*
   * If the app-prefab properties are not available then load them and check for version mismatch
   */
  registerPrefab(prefabName: string, forceRegister?: boolean) {
    if (isEmpty(this.appPrefabNamePropertiesMap) || !this.appPrefabNamePropertiesMap) {
      return this.listAppPrefabs().then(() => this.validateAndRegister(prefabName, forceRegister));
    }
    return this.validateAndRegister(prefabName, forceRegister);
  }

  /*
   * Force register prefab and if the app-prefab properties are not available then load them and check for version mismatch
   */
  forceRegisterPrefab(prefabName: string, callback: () => any, forceRegister: boolean, versionId: string) {
    if (this.appPrefabNamePropertiesMap === undefined) {
      this.listAppPrefabs().then(() => this.validateAndRegister(prefabName, forceRegister, versionId).then(callback));
    } else {
      this.validateAndRegister(prefabName, forceRegister, versionId).then(callback);
    }
  }

  deRegisterPrefab(projectId: string, prefabName: string) {
    return this.prefabService.deRegister({
      projectId: projectId,
      data: {
        prefabName: prefabName
      }
    }).then((response) => {
      delete this.appPrefabNamePropertiesMap[prefabName];
      return Promise.resolve(response);
    }, (error) => {
      return Promise.reject(error);
    });
  }

  onAppPrefabsLoad(prefabs: Array<any>) {
    prefabs.forEach((prefab: any) => {
      this.appPrefabNamePropertiesMap[prefab.name] = prefab;
    });
  }

  listAppPrefabs() {
    this.appPrefabNamePropertiesMap = {};

    return this.pageService.fetchContent('services/projects/' + this.projectService.getProjectId() + '/prefabs')
      .then((response: any) => {
      this.onAppPrefabsLoad(response);
    }, (error: any) => {
      // wmToaster.show('error', $rs.locale.MESSAGE_ERROR_TITLE, $rs.locale.MESSAGE_ERROR_PROJECT_PREFAB_LIST_FAILED_DESC);
    });
  }

  /* load external scripts required by the prefab */
  loadScripts(scripts: Array<string>) {
    return new Promise<void>((resolve, reject) => {
    let rand = Date.now(),
      count = 0,
      fnName = 'onPrefabResourceLoad' + rand,
      obj: any,
      intervalHandler: any;

    scripts = scripts.map((script: string) => {
      if (script.indexOf('onPrefabResourceLoad') !== -1) {
        count += 1;
        script = script.replace('onPrefabResourceLoad', fnName);
      }
      return script;
    });


    obj = new OnPrefabResourceLoad(count);

    // @ts-ignore
    window[fnName] = obj.updateCount.bind(obj);

    if (!scripts || !scripts.length) {
      resolve();
    } else {
      _loadScripts(scripts).then(resolve, reject);
    }
    });
  }

  getProjectPrefab(prefabName: string) {
    return getClonedObject(this.appPrefabNamePropertiesMap[prefabName]) || {};
  }

  getWorkspacePrefab(prefabName: string) {
    return getClonedObject(this.studioPrefabNamePropertiesMap[prefabName]) || {};
  }

  isPrefabVersionMismatch(prefabName: string) {
    let prefab: any = this.getProjectPrefab(prefabName),
      prefabObj: any = this.getWorkspacePrefab(prefabName);

    if ((keys(prefabObj).length > 0 && (prefabObj.status === 'DEPRECATED' || prefab.artifactStatus === 'DEV')) || (prefab.workspaceVersion && (prefab.version !== prefab.workspaceVersion || prefab.artifactStatus === 'DEV')) || (prefab.workspaceVersion === null && prefab.artifactStatus === 'PUBLISHED' && prefabObj.status === 'PUBLISHED' && prefab.version != prefabObj.version)) {
      return true;
    } else {
      return false;
    }
  }

  validateAndRegister(prefabName: string, forceRegister?: boolean, versionId?: string):any {
    if (!this.appPrefabNamePropertiesMap[prefabName] || (this.isPrefabVersionMismatch(prefabName) && forceRegister)) {
      if (versionId) {
        return this.updatePrefabVersion(prefabName, forceRegister, versionId);
      } else if(!forceRegister){
        // prefab never registered.
        return this.doRegistration(prefabName, forceRegister);
      }
    } else if (this.isPrefabVersionMismatch(prefabName)) {
      // TODO showPrefabUpgradeDialog
      // prefab is registered but the version of studio-prefab is not same as app-prefab
      return this.showPrefabUpgradeDialog(prefabName);
    }else{
      // registration is not required
      return Promise.resolve();
    }

  }

  doRegistration(prefabName: string, forceRegister?: boolean) {

    /* register the prefab & then add the widget to canvas */
    return this.prefabService.register({
      projectId: this.projectService.getProjectId(),
      prefabId: this.studioPrefabNamePropertiesMap[prefabName].id,
      versionId: this.studioPrefabNamePropertiesMap[prefabName].versionId,
      forceRegister: forceRegister ? true : false
    }).then(() => {
      this.appPrefabNamePropertiesMap[prefabName] = getClonedObject(this.studioPrefabNamePropertiesMap[prefabName]);
      this.appPrefabNameConfigMap[prefabName] = this.studioPrefabNameConfigMap[prefabName];
      // Refresh the prefabs list
      //$rs.$emit('list-prefabs');
      this.loadPrefabs();
    }, (e) => {
      return Promise.resolve(e);
    });
  }

  isPrefabVersionConflictError(errObj: any) {
    let errKey = get(errObj, 'errors.error[0].messageKey');
    return errKey === 'com.wavemaker.platform.project$DependentPrefabExistWithDifferentVersion';
  }

  isCircularDependencyError(errObj: any) {
    let errKey = get(errObj, 'errors.error[0].messageKey');
    return errKey === 'com.wavemaker.studio.prefab.circularDependencyNotAllowed';
  }


  prepareUpdatePrefabVerData(prefabName: string, versionId: string, forceRegister: boolean) {
    return {
      "studioProjectId": this.projectService.getProjectId(),
      "studioArtifactVersion": {
        "artifactId": this.studioPrefabNamePropertiesMap[prefabName].id,
        "versionId": versionId
      },
      "forceRegister": forceRegister ? true : false,
      "upgrade": true
    };
  }

  updatePrefabVersion(prefabName: string, forceRegister: boolean = false, versionId: string) {
    let data = this.prepareUpdatePrefabVerData(prefabName, versionId, forceRegister);
    this.artifactsService.updatePrefabVersion(this.studioPrefabNamePropertiesMap[prefabName].id, data)
    // TODO ArtifactsManager
    // ArtifactsManager.updatePrefabVersion($rs.teamProjectObject.id, this.studioPrefabNamePropertiesMap[prefabName].id, data, function () {
    //   Utils.triggerFn(callback);
    //   // Refresh the prefabs list
    //   $rs.$emit('list-prefabs');
    // }, function (err) {
    //   Utils.triggerFn(callback, err);
    // });
    return Promise.resolve();
  }
  //
  showPrefabUpgradeDialog(prefabName: string) {
    this.upgradePrefabDetails = this.getProjectPrefab(prefabName);
    let wsPrefab: any = this.getWorkspacePrefab(prefabName);
    this.upgradePrefabDetails.conflictMessage = this.constructPrefabConflictMessage(this.upgradePrefabDetails, wsPrefab);
    return new Promise<void>((resolve) => {
      this.dialogService.open('upgradePrefabDialog', {
        resolve: {
          upgradeFn: () => {
            const data = this.prepareUpdatePrefabVerData(wsPrefab.name, wsPrefab.versionId, false);
            this.artifactsService.updatePrefabVersion(this.studioPrefabNamePropertiesMap[prefabName].id, data).then(() => {
            resolve();
            this.dialogService.close('upgradePrefabDialog');
              setTimeout(() => {
              this.wsManager.saveActiveWs.next(() => {
                window.location.reload();
              });
              }, 1000);
            });
          },
          onSkipUpgrade: () => {
            this.dialogService.close('upgradePrefabDialog');
            resolve();
          },
        },
      });
    })
  }

  loadAppPrefabConfig(prefabName: string) {
    let configUrl: string,
      config;

    config = this.appPrefabNameConfigMap[prefabName];

    if (config) {
      return Promise.resolve(config);
      // Utils.triggerFn(callback, config);
    } else {
      return this.prefabCacheService.getPrefabDataByType(
        {type: 'config', prefabName}).then((response: any) => {
          this.appPrefabNameConfigMap[prefabName] = response;
          this.prefabService.setPrefabConfig(prefabName, response)
          return response;
        },
        () => {
          // wmToaster.show('error', $rs.locale.MESSAGE_PREFAB_CONFIG_ERROR_TITLE, $rs.locale.MESSAGE_PREFAB_CONFIG_ERROR_DESC);
        });
    }
  }

  cachePrefabScript(prefabName: string, script: any) {
    this.prefabScriptCache[prefabName] = script;
  }

  loadPrefabMinifiedPage(prefabName: string) {
    return new Promise((resolve, reject) => {
      /*read the file content*/
      this.resourceMngr.getPageDataForPrefab(prefabName).then((prefabContent: any) => {
        if (isString(prefabContent)) {
          prefabContent = JSON.parse(prefabContent);
        }
        let htmlMarkup = getDecodedData(prefabContent.markup) || '',
          PREFAB_VARIABLE_NAMESPACE: string = '_MainPage_Variables_';
        /*load the styles & scripts*/

        this.cachePrefabScript(prefabName, getDecodedData(prefabContent.script));

        $('head').append('<style>' + getDecodedData(prefabContent.styles) + '</style>');
        set(window, PREFAB_VARIABLE_NAMESPACE, getValidJSON(getDecodedData(prefabContent.variables)) || {});
        /* append the isPrefab flag to each variable */
        forEach(get(window, PREFAB_VARIABLE_NAMESPACE), (variable) => {
          variable._prefabName = prefabName;
        });
        /* set variables in prefab namespace, registration will occur in page directive */
        this.variableService.setPageVariables(prefabName, get(window, PREFAB_VARIABLE_NAMESPACE), 1);
        resolve(htmlMarkup);
      });
    });
  }

  loadPrefabDependencies(prefabName: string) {
    this.prefabService.getAppPrefabServerProps({
      'prefabName': prefabName
    })
  }

  loadDependencies(prefabName: string): Promise<string> {
    return new Promise((resolve, reject) => {
    let config = this.appPrefabNameConfigMap[prefabName],
      resources,
      resourcePath: string,
      scriptFiles,
      styleFiles,
      modules,
      handler: Subscription;

    if (!config) {
      return reject();
    }

    resources = config.resources || {};

    resourcePath = 'services/projects/' + this.projectService.getProjectId() + '/resources/info/web/WEB-INF/prefabs/' + prefabName + '/webapp/';

    if (this.pendingTasks['resources'][prefabName]) {
      handler = runtimeEventManager.on(prefabName + '-dependencies-ready').subscribe((prefabContent: any) => {
        handler.unsubscribe();
        resolve(prefabContent);
      });
      this.registerDestroyListener.add(handler);
    } else {
      this.pendingTasks['resources'][prefabName] = true;

      scriptFiles = resources.scripts || [];
      styleFiles = resources.styles || [];
      modules = resources.modules || [];

      scriptFiles = scriptFiles.map((file: string) => {
        return this.getPrefabResourceUrl(file, resourcePath, true);
      });

      modules = modules.map((module: any) => {
        module.files = module.files.map((file: string) => {
          return this.getPrefabResourceUrl(file, resourcePath, true);
        });
        return module;
      });

      styleFiles = styleFiles.map((file: string) => {
        if (!stringEndsWith(file, '/pages/Main/Main.css')) {
          return this.getPrefabResourceUrl(file, resourcePath, true);
        }
        return undefined;
      }).filter((file: string) => {
        return !!file;
      });

      loadStyleSheets(styleFiles);
      scriptFiles = scriptFiles.filter((file: string) => !file.startsWith('npm://'));
      this.loadScripts(scriptFiles)
        .then(this.loadPrefabMinifiedPage.bind(this, prefabName))
        .then((prefabContent: any) => {
          this.pendingTasks['resources'][prefabName] = undefined;
          runtimeEventManager.emit(prefabName + '-dependencies-ready', prefabContent);
          resolve(prefabContent);
        });
    }
    });
  }

  getPrefabResourceUrl(resourcePath: string, resourceBasePath: string, preventCache: boolean = false) {
    var _url = resourcePath;
    if (!stringStartsWith(resourcePath, 'http://|https://|//')) {
      _url = (resourceBasePath + _url).replace('//', '/');
    }
    if (preventCache) {
      _url = preventCachingOf(_url);
    }

    return _url;
  }

  updateHTMLImportRefs(prefabName: string, markup: string) {
    let $markup = $(markup);
    $markup.find('link[rel=import]')
      .each(() => {
        // todo
        let $el  = $(this),
          href = $el.attr('href'),
          resourcePath;

        resourcePath = 'services/projects/' + this.projectService.getProjectId() + '/resources/info/web/WEB-INF/prefabs/' + prefabName + '/webapp/';

        if (href) {
          $el.attr('href', this.getPrefabResourceUrl(href, resourcePath));
        }
      });
    return $markup[0].outerHTML;
  }



  constructPrefabConflictMessage(prefab: any, wsPrefab?: any) {
    let msg:any, actionsBtnMsg = 'Update', pubV:any, curV:any, latestVersion;
    curV = prefab.version;
    pubV = prefab.workspaceVersion;
    this.selectedOrganization = this.studioService.selectedOrganization

    if (prefab.version && prefab.version.split('.').length && prefab.workspaceVersion && prefab.workspaceVersion.split('.').length) {
      if (isNewVerGreaterThanCurrentVer(curV, pubV)) {
        msg = this.getLocalizedMessage('MESSAGE_PREFAB_VERSION_MISMATCH_OLDER', prefab.name, prefab.version, prefab.workspaceVersion, actionsBtnMsg);
      } else {
        actionsBtnMsg = 'Revert';
        msg = this.getLocalizedMessage('MESSAGE_PREFAB_VERSION_MISMATCH_FUTURE', prefab.name, prefab.version, prefab.workspaceVersion, actionsBtnMsg);
      }
    } else {
      if (prefab.version !== prefab.workspaceVersion) {
        msg = this.getLocalizedMessage('MESSAGE_PREFAB_VERSION_MISMATCH_CONFLICT', prefab.name, prefab.workspaceVersion, prefab.version, actionsBtnMsg);
      }
    }
    if (prefab.artifactStatus === 'DEV') {
      msg = this.getLocalizedMessage('MESSAGE_PREFAB_VERSION_MISMATCH_DEVELOPMENT', prefab.name, prefab.version, prefab.workspaceVersion, actionsBtnMsg);
    }

    if (this.selectedOrganization.mode === 'ORG' || this.preferencesService.get('workspace.wmEnv') === 'WME') {
      msg = this.getLocalizedMessage('MESSAGE_ORG_PREFAB_VERSION_MISMATCH_OLDER', prefab.name);
      actionsBtnMsg = "Update";
      if (prefab.artifactStatus === 'DEV') {
        latestVersion = get(prefab, 'versionList[0].artifactVO.version') || get(wsPrefab, 'version') || '';
        // If current verison is less than latest version, show update btn else show revert btn
        if (isNewVerGreaterThanCurrentVer(prefab.version, latestVersion)) {
          actionsBtnMsg = 'Update';
          msg = this.getLocalizedMessage('MSG_ORG_PREFAB_VERSION_MISMATCH_DEVELOPMENT_UPDATE', prefab.name, actionsBtnMsg);
        } else if (isNewVerGreaterThanCurrentVer(latestVersion, prefab.version)) {
          actionsBtnMsg = 'Revert';
          msg = this.getLocalizedMessage('MSG_ORG_PREFAB_VERSION_MISMATCH_DEVELOPMENT_UPDATE', prefab.name, actionsBtnMsg);
        } else {
          actionsBtnMsg = 'Revert';
          msg = this.getLocalizedMessage('MSG_ORG_PREFAB_VERSION_MISMATCH_DEVELOPMENT_REVERT', prefab.name);
        }
      } else if (prefab.wkspPrefabWithHigherVers || prefab.wkspPrefabPublishToTeam) {
        latestVersion = get(prefab, 'versionList[0].artifactVO.version') || prefab.workspaceVersion;
        if (isNewVerGreaterThanCurrentVer(prefab.version, latestVersion)) {
          actionsBtnMsg = 'Update';
          msg = prefab.wkspPrefabWithHigherVers ? this.getLocalizedMessage('MESSAGE_PREFAB_VERSION_MISMATCH_OLDER', prefab.name, prefab.version, latestVersion, actionsBtnMsg) : this.getLocalizedMessage('MESSAGE_ORG_PREFAB_VERSION_MISMATCH_OLDER', prefab.name);
        } else if (isNewVerGreaterThanCurrentVer(latestVersion, prefab.version)) {
          actionsBtnMsg = 'Revert';
          msg = prefab.wkspPrefabWithHigherVers ? this.getLocalizedMessage('MESSAGE_PREFAB_VERSION_MISMATCH_FUTURE', prefab.name, prefab.version, latestVersion, actionsBtnMsg) : this.getLocalizedMessage('MESSAGE_ORG_PREFAB_VERSION_MISMATCH_FUTURE', prefab.name);
        }
      }
    }
    prefab.actionsBtnMsg = actionsBtnMsg;
    return msg;

  }

  getLocalizedMessage(message: any, ...args: any) {
    let key = this.locale[message];
    return replace(key, args);
  }
}
